import fetchRequestProducts from 'app/apis/fetchRequestProducts'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { ProductCatalogListing } from 'app/models/RequestFlow'
import { useCallback, useEffect, useRef, useState } from 'react'

const mergeProductPages = (
  prev: ProductCatalogListing[],
  incoming: ProductCatalogListing[],
) => {
  if (!incoming.length) return prev
  const existingIds = new Set(prev.map(item => item.id))
  const uniqueIncoming = incoming.filter(item => !existingIds.has(item.id))
  if (!uniqueIncoming.length) return prev
  return [...prev, ...uniqueIncoming]
}

const useFetchProducts = ({
  vendor_id,
  category_id,
  brand_id,
  search_query,
  role_type,
}: {
  vendor_id?: number
  category_id?: string
  brand_id?: string
  search_query?: string
  role_type?: string
}) => {
  const [request, setRequest] = useState<ProductCatalogListing[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingNext, setLoadingNext] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isFetchingNextRef = useRef(false)
  const pageRef = useRef(1)
  const hasMoreRef = useRef(false)

  const fetchProducts = useCallback(
    async (page: number) => {
      return fetchRequestProducts(
        page,
        vendor_id,
        category_id,
        brand_id,
        search_query,
        role_type,
      )
    },
    [vendor_id, category_id, brand_id, search_query, role_type],
  )

  const getRequest = useCallback(async () => {
    setLoading(true)
    setError(null)
    setRequest([])
    setHasMore(false)
    hasMoreRef.current = false
    pageRef.current = 1
    isFetchingNextRef.current = false
    try {
      const response = await fetchProducts(1)
      if (response?.response?.status && response?.response?.status === 400) {
        setError('Something went wrong')
        ToastAndroid.show(response.response.data.detail, ToastAndroid.SHORT)
        setRequest([])
      } else if (Array.isArray(response?.results)) {
        setRequest(response.results)
        const more = Boolean(response.next)
        setHasMore(more)
        hasMoreRef.current = more
      } else {
        setError('Something went wrong')
        setRequest([])
        if (response?.detail) {
          ToastAndroid.show(response.detail, ToastAndroid.SHORT)
        } else {
          ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
        }
      }
    } catch (e) {
      setError('Something went wrong')
      setRequest([])
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
    } finally {
      setLoading(false)
    }
  }, [fetchProducts])

  const fetchNext = useCallback(async () => {
    if (!hasMoreRef.current || isFetchingNextRef.current) return false

    try {
      isFetchingNextRef.current = true
      setLoadingNext(true)
      const nextPage = pageRef.current + 1
      const response = await fetchProducts(nextPage)
      if (Array.isArray(response?.results)) {
        pageRef.current = nextPage
        setRequest(prev => mergeProductPages(prev, response.results))
        const more = Boolean(response.next)
        setHasMore(more)
        hasMoreRef.current = more
        return true
      }
      setError('Something went wrong')
      setHasMore(false)
      hasMoreRef.current = false
      if ((response as any)?.detail) {
        ToastAndroid.show((response as any).detail, ToastAndroid.SHORT)
      } else {
        ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
      }
      return false
    } catch (e) {
      setError('Something went wrong')
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
      return false
    } finally {
      isFetchingNextRef.current = false
      setLoadingNext(false)
    }
  }, [fetchProducts])

  useEffect(() => {
    getRequest()
  }, [getRequest])

  return {
    request,
    loading,
    loadingNext,
    hasMore,
    error,
    fetchNext,
    refetch: getRequest,
  }
}

export default useFetchProducts
